import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { isEmpty, isFunction, isUndefined, partial, get } from 'lodash-es'
import { intl } from 'lang/intl'

import { useWebinarColors } from '../../../../hooks/useWebinarColors'
import {
  Button as ButtonComponent,
  ConditionWrapComponent,
  ConfirmComponent,
  PollItemComponent,
} from '../../../../components'
import { intToBool } from '../../../../utils'
import { getPollState } from '../../utils'
import { PollOverlayTrigger } from '..'

import './PollItemWrapperComponent.scss'

const getWasPollStarted = (poll) => !isEmpty(poll.opened_date)
const getIsPollOpen = (poll) => intToBool(poll.open_p)
const getIsPollPublished = (poll) => intToBool(poll.display_results_p)

const getTitle = (poll) => {
  const dot = `·`
  const count = parseInt(poll.answer_count, 10)

  const singularize = intl.formatMessage(
    { defaultMessage: `{count} vote` },
    { count }
  )

  const pluralize = intl.formatMessage(
    { defaultMessage: `{count} votes` },
    { count }
  )

  const votes = count === 1 ? singularize : pluralize

  if (getIsPollOpen(poll)) {
    const message = intl.formatMessage({ defaultMessage: `Open for voting` })
    return `${message} ${dot} ${votes}`
  }

  if (getIsPollPublished(poll)) {
    const message = intl.formatMessage({ defaultMessage: `Results showing` })
    return `${message} ${dot} ${votes}`
  }

  const prefix = intl.formatMessage({ defaultMessage: `Poll` })
  const suffix = intl.formatMessage({ defaultMessage: `Not started` })

  if (getWasPollStarted(poll)) {
    return `${prefix} · ${votes}`
  }

  return `${prefix} · ${suffix}`
}

const renderOverlayTrigger = (opts) => {
  const { styles, poll } = opts

  if (isEmpty(poll.opened_date)) {
    return null
  }

  if (opts.disableOverlayTrigger) {
    return null
  }

  const accentColor = get(styles, 'progressBarStyles.strokeColor')
  const passiveColor = get(styles, 'radioButtonStyles.passiveColor')

  return (
    <PollOverlayTrigger
      poll={poll}
      accentColor={accentColor}
      passiveColor={passiveColor}
    />
  )
}

const getHeaderAction = (poll, opt) => {
  const { deletePoll, editPoll, canChangePoll } = opt

  if (!canChangePoll) {
    return null
  }

  return (
    <div className="header-actions-wrapper">
      <div className="buttons-wrapper">
        {intToBool(poll.editable_p) && (
          <div onClick={editPoll} className="edit-poll">
            <FormattedMessage defaultMessage="Edit" />
          </div>
        )}
        <ConfirmComponent
          onConfirm={deletePoll}
          confirmText={intl.formatMessage({
            defaultMessage: 'Are you sure you want to delete this poll?',
          })}
          okText={intl.formatMessage({ defaultMessage: 'Yes, Delete Poll' })}
          cancelText={intl.formatMessage({ defaultMessage: 'Cancel' })}
        >
          <div className="delete-button">
            <FormattedMessage defaultMessage="Delete" />
          </div>
        </ConfirmComponent>
      </div>
      {renderOverlayTrigger(opt)}
    </div>
  )
}

const getPollItemWrapper = (opt, poll, children) => {
  const { renderPollUnitWrapper } = opt

  return renderPollUnitWrapper({
    title: getTitle(poll),
    isPollActive: getIsPollOpen(poll) || getIsPollPublished(poll),
    renderHeaderAction: () => getHeaderAction(poll, opt),
    children,
  })
}

const getConfirmDeactivatePollText = (activePoll, poll) => (
  <span>
    <FormattedMessage
      defaultMessage='You are about to activate "{poll}", which will also deactivate "{active_poll}". Do you want to continue?'
      values={{
        poll: <b>{poll.question}</b>,
        active_poll: <b>{activePoll.question}</b>,
      }}
    />
  </span>
)

const getPollButton = (colors, pollState, onClick) => {
  const pollButtonTextMap = {
    start: intl.formatMessage({ defaultMessage: 'Start Poll' }),
    finish: intl.formatMessage({ defaultMessage: 'Close Poll' }),
    publish: intl.formatMessage({ defaultMessage: 'Show Results' }),
    unpublish: intl.formatMessage({ defaultMessage: 'Stop Showing Results' }),
  }

  return (
    <ButtonComponent
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.textColor,
      }}
      onClick={onClick}
      color="transparent"
      className="poll-button"
      round
    >
      {pollButtonTextMap[pollState] || ''}
    </ButtonComponent>
  )
}

const renderPollButton = (opt) => {
  const { poll, activePoll, colors, activatePoll } = opt

  const pollState = getPollState(poll)
  const pollId = poll.poll_id
  const isOtherPollActive =
    !isUndefined(activePoll) && activePoll.poll_id !== pollId
  const buttonBase = partial(getPollButton, colors, pollState)
  const isPollStateFinish = pollState === 'finish'

  if (!isOtherPollActive && !isPollStateFinish) {
    return buttonBase(activatePoll)
  }

  return (
    <ConfirmComponent
      onConfirm={activatePoll}
      okText={
        isPollStateFinish
          ? intl.formatMessage({ defaultMessage: 'Yes, close it' })
          : intl.formatMessage({ defaultMessage: 'Yes' })
      }
      confirmText={
        isOtherPollActive
          ? getConfirmDeactivatePollText(activePoll, poll)
          : intl.formatMessage({
              defaultMessage:
                'You are about to close this poll. This means that you won’t be able to start it again to collect more votes.',
            })
      }
      cancelText={intl.formatMessage({ defaultMessage: 'Cancel' })}
    >
      {buttonBase()}
    </ConfirmComponent>
  )
}

const PollItemWrapperComponent = (props) => {
  const {
    poll,
    renderPollUnitWrapper,
    styles,
    activePoll,
    activatePoll,
    canChangePoll,
    isVotingAllowed,
    answerPoll,
  } = props

  const { accent } = useWebinarColors()
  const colors = {
    backgroundColor: accent.color,
    textColor: accent.contrast.color,
  }

  return (
    <div className="poll-item-wrapper-component">
      <ConditionWrapComponent
        condition={isFunction(renderPollUnitWrapper)}
        wrap={partial(getPollItemWrapper, props, poll)}
      >
        <PollItemComponent
          poll={poll}
          progressBarStyles={get(styles, 'progressBarStyles')}
          radioButtonStyles={get(styles, 'radioButtonStyles')}
          isVotingAllowed={isVotingAllowed}
          answerPoll={answerPoll}
        />
        {canChangePoll &&
          renderPollButton({
            poll,
            activePoll,
            colors,
            activatePoll,
          })}
      </ConditionWrapComponent>
    </div>
  )
}

PollItemWrapperComponent.propTypes = {
  poll: PropTypes.object,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  style: PropTypes.object,
  renderPollUnitWrapper: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  editPoll: PropTypes.func,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  deletePoll: PropTypes.func,
  styles: PropTypes.shape({
    inputStyle: PropTypes.object,
    progressBarStyles: PropTypes.object,
    radioButtonStyles: PropTypes.object,
  }),
  activePoll: PropTypes.object,
  activatePoll: PropTypes.func,
  answerPoll: PropTypes.func,
  canChangePoll: PropTypes.bool,
  isVotingAllowed: PropTypes.bool,
  // TODO (noahlaux): Refactor with TS
  // eslint-disable-next-line react/no-unused-prop-types
  disableOverlayTrigger: PropTypes.bool,
}

export default PollItemWrapperComponent
